import { Column, PluginHook, Row, useFlexLayout, useTable } from 'react-table'
import React, { ElementType, FC, useCallback, useContext, useEffect, useState } from 'react'
import { VariableSizeList, ListChildComponentProps, ListOnItemsRenderedProps } from 'react-window'
import Tooltip from 'react-tooltip'
import { debounce } from 'lodash'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Translate, I18n } from 'react-redux-i18n'
import { TableContext } from './contexts/TableContext'

export interface VirtualizedTableProps<T extends object> {
  columns: Array<Column<T>>
  data: Array<T>
  SubComponent?: ElementType
  getItemSize: (index: number) => number
  plugins?: Array<PluginHook<T>>
  className?: string
}

const RenderRow: FC<ListChildComponentProps> = ({
  index,
  style,
  data: { rows, prepareRow, SubComponent },
}) => {
  const row: Row<any> = rows[index]
  prepareRow(row)
  const rowProps = row.getRowProps({
    style,
  })
  const {
    listRef,
    expandedPositionsState,
    isRowActive,
    keyPropertyName,
    rowClickHandle,
  } = useContext(TableContext)

  const isExpanded = expandedPositionsState[row.original[keyPropertyName]]
  useEffect(() => {
    if (listRef && listRef.current) {
      listRef.current.resetAfterIndex(index)
    }
  }, [listRef, index, isExpanded])

  return (
    <div
      {...rowProps}
      className="rt-tr-group"
      role="rowgroup"
      onClick={rowClickHandle}
      data-symbol={row.original.symbol}
    >
      <div
        className={`rt-tr${
          isRowActive && isRowActive(row.original[keyPropertyName]) ? ' active' : ''
        }`}
      >
        {row.cells.map((cell, idx) => {
          return (
            <div {...cell.getCellProps()} key={idx}>
              {cell.render('Cell')}
            </div>
          )
        })}
      </div>
      {SubComponent && <SubComponent row={row.original} />}
    </div>
  )
}

interface VFC<T extends object> extends FC<VirtualizedTableProps<T>> {}

const rebuildTooltip = debounce(() => Tooltip.rebuild(), 200, {
  leading: false,
  trailing: true,
})

export const VirtualizedTable: VFC<{}> = ({
  data,
  columns,
  SubComponent,
  getItemSize,
  plugins = [],
  className,
}) => {
  const defaultColumn = React.useMemo(
    () => ({
      width: 150,
    }),
    []
  )
  const { getTableProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data, defaultColumn },
    ...plugins,
    useFlexLayout
  )
  const { listRef } = useContext(TableContext)

  const [hasScroll, setHasScroll] = useState(false)
  const changeScrollState = useCallback(
    ({ visibleStartIndex, visibleStopIndex, overscanStopIndex }: ListOnItemsRenderedProps) => {
      setHasScroll(overscanStopIndex > visibleStopIndex || visibleStartIndex !== 0)
    },
    []
  )
  return (
    <div
      {...getTableProps()}
      className={`ReactTable bdswiss-table rt-table ${className ? className : ''}`}
    >
      <AutoSizer onResize={rebuildTooltip}>
        {({ height, width }) => (
          <>
            <div
              className="rt-thead rt-th-group"
              style={{ width, paddingRight: hasScroll ? '2rem' : '1rem' }}
            >
              {headerGroups.map(headerGroup => (
                <div {...headerGroup.getHeaderGroupProps()} className="rt-tr">
                  {headerGroup.headers.map(column => {
                    const header = column.Header
                    return (
                      <div {...column.getHeaderProps()} className="rt-th" role="columnheader">
                        {typeof header === 'string' ? (
                          <span title={I18n.t(header)}>
                            <Translate value={header} />
                          </span>
                        ) : (
                          column.render('Header')
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
            <VariableSizeList
              onScroll={rebuildTooltip}
              height={height - 36}
              itemCount={data.length}
              estimatedItemSize={35}
              itemSize={getItemSize}
              width={width}
              className="rt-tbody"
              itemData={{ rows, prepareRow, SubComponent }}
              ref={ref => {
                listRef && (listRef.current = ref)
              }}
              onItemsRendered={changeScrollState}
            >
              {RenderRow}
            </VariableSizeList>
          </>
        )}
      </AutoSizer>
    </div>
  )
}
